import type { ComponentType, SVGAttributes } from "react";
import { FileIcon, ModelIcon } from "@hightouch/htui";
import { ContextFileType } from "src/graphql/types";

/**
 * Config that lets the context-entities table and detail page serve more than one
 * Knowledge surface. The Files tab uses {@link CONTEXT_SURFACE}; the Skills tab
 * uses {@link SKILLS_SURFACE}. The two differ only in the copy they show,
 * whether the entity is force-classified on create, and whether a scope column
 * is shown. The underlying entity plumbing is identical.
 */
export type ContextSurface = {
  /**
   * Classification applied to entities created on this surface, and used to
   * hide the manual context-type selector (it's fixed). Undefined = the Files
   * tab, which shows the selector and creates unclassified entries.
   */
  forcedContextType?: ContextFileType;
  noun: string;
  nounPlural: string;
  /** Shown in each row's name tile. */
  icon: ComponentType<SVGAttributes<SVGElement>>;
  /**
   * When set, the table renders a Scope column showing this label for
   * workspace entities, and "Global" for Hightouch-managed ones.
   */
  scopeLabel?: string;
  /** The detail page's section label and its list's path segment. */
  section: { label: string; path: string };
  /** An example for the usage guidance field. */
  usageGuidancePlaceholder: string;
};

export const CONTEXT_SURFACE: ContextSurface = {
  noun: "file",
  nounPlural: "files",
  icon: FileIcon,
  section: { label: "Files", path: "files" },
  usageGuidancePlaceholder:
    "e.g. Use when writing customer-facing copy for ads, emails, or social posts.",
};

export const SKILLS_SURFACE: ContextSurface = {
  forcedContextType: ContextFileType.Skill,
  noun: "skill",
  nounPlural: "skills",
  icon: ModelIcon,
  scopeLabel: "Workspace",
  section: { label: "Skills", path: "skills" },
  usageGuidancePlaceholder:
    "e.g. Use when writing headlines for Meta ads. Not for email subject lines.",
};
